import { useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    GROUP_ACCOUNT,
    ROUTE_ACCOUNT_ACADEMY,
    ROUTE_ACCOUNT_DATA,
    ROUTE_ACCOUNT_DOWNLOAD,
    ROUTE_ACCOUNT_DOWNLOAD_INFO_TOPIC,
    ROUTE_ACCOUNT_SUCCESS_LIST,
    ROUTE_ACCOUNT_SUCCESS_LIST_OVERVIEW,
    ROUTE_ACCOUNT_SUCCESS_LIST_TOPICS_GROUP,
    ROUTE_ACCOUNT_VIDEO,
    ROUTE_VIDEO_TOPICS_GROUP,
} from 'src/config/account-routes';
import { AppRoute } from 'src/core/AppRoute';
import { SiteLayoutContext } from 'src/core/provider/SiteLayoutContext';
import { CustomerDownloadTopic } from 'src/entities/customer/CustomerDownload';
import SuccessListTopic from 'src/entities/success-list/SuccessListTopic';
import VideoTopic from 'src/entities/video/VideoTopic';
import { getCustomerData } from 'src/store/customer/data/selectors';
import { getCustomerDownloadTopics } from 'src/store/customer/downloads/selectors';
import { getSuccessListTopics } from 'src/store/success-list/selectors';
import { getVideoTopics } from 'src/store/video/selectors';

interface ComposedRoutes {
    routes: AppRoute[];
    first: AppRoute;
    parent: AppRoute;
}

export function useDownloadInfoTopicRoutes(): ComposedRoutes {
    const downloadTopics = useSelector(getCustomerDownloadTopics);

    const [routes, first] = useMemo(() => {
        const routes = downloadTopics.items.map((topic: CustomerDownloadTopic) => {
            const route = ROUTE_ACCOUNT_DOWNLOAD_INFO_TOPIC.interpolatedClone({ topic: topic.normalizedName });
            route.t_params = { topic: topic.localizedName };

            return route;
        });
        return [routes, routes[0]];
    }, [downloadTopics.items]);

    const parent = ROUTE_ACCOUNT_DOWNLOAD;

    return {
        routes,
        first,
        parent,
    };
}

export function useVideoTopicRoutes(): ComposedRoutes {
    const videoTopics = useSelector(getVideoTopics);

    const [routes, first] = useMemo(() => {
        let first;
        const routes = videoTopics.items.map((topic: VideoTopic) => {
            const route = ROUTE_VIDEO_TOPICS_GROUP.interpolatedClone({ videoTopic: topic.normalizedName });
            route.t_params = { videoTopic: topic.localizedName };
            route.infoBadgeCount = topic.unratedVideoCount;

            if (first === undefined && topic.unratedVideoCount > 0) first = route;

            return route;
        });
        return [routes, first ?? routes[0]];
    }, [videoTopics.items]);

    const infoBadgeCount = useMemo(() => videoTopics.items.reduce((sum, t) => sum + t.unratedVideoCount, 0), [
        videoTopics.items,
    ]);

    const parent = ROUTE_ACCOUNT_VIDEO;
    parent.infoBadgeCount = infoBadgeCount;

    return {
        routes,
        first,
        parent,
    };
}

export function useSuccessListTopicRoutes(): ComposedRoutes {
    const successListTopics = useSelector(getSuccessListTopics);

    const [routes, first] = useMemo(() => {
        let first;
        const routes = successListTopics.items.map((topic: SuccessListTopic) => {
            let route;

            switch (topic.normalizedName) {
                case 'OVERVIEW':
                    route = ROUTE_ACCOUNT_SUCCESS_LIST_OVERVIEW;
                    break;
                case 'LEGACY':
                    route = ROUTE_ACCOUNT_ACADEMY;
                    break;
                default:
                    route = ROUTE_ACCOUNT_SUCCESS_LIST_TOPICS_GROUP.interpolatedClone({
                        successListTopic: topic.normalizedName,
                    });
                    route.t_params = { successListTopic: topic.localizedName };
                    if (topic.normalizedName === 'CURRENT') route.infoBadgeCount = topic.nameListEntryCount;
            }

            if (first === undefined && topic.nameListEntryCount > 0) first = route;

            return route;
        });
        return [routes, first ?? ROUTE_ACCOUNT_SUCCESS_LIST_OVERVIEW];
    }, [successListTopics.items]);

    const parent = ROUTE_ACCOUNT_SUCCESS_LIST;
    parent.infoBadgeCount = successListTopics.items.find(x => x.normalizedName === 'CURRENT')?.nameListEntryCount;

    return {
        routes,
        first,
        parent,
    };
}

interface AccountRoutes {
    routes: (AppRoute | AppRoute[])[];
    downloadInfoTopicRoutes: AppRoute[];
    firstDownloadInfoTopicRoute: AppRoute;
    videoTopicRoutes: AppRoute[];
    firstVideoTopicRoute: AppRoute;
    successListTopicsRoutes: AppRoute[];
    firstSuccessListTopicRoute: AppRoute;
}

export function useAccountRoutes(): AccountRoutes {
    const downloadInfoTopics = useDownloadInfoTopicRoutes();
    const videoTopicRoutes = useVideoTopicRoutes();
    const successListTopicRoutes = useSuccessListTopicRoutes();
    const customerData = useSelector(getCustomerData);
    const { allowedAccountRoutePaths } = useContext(SiteLayoutContext);

    const allRoutes = GROUP_ACCOUNT.map(route => {
        if (Array.isArray(route)) {
            if (route[0].path === ROUTE_ACCOUNT_DATA.path) {
                route[0].infoBadgeCount = 33;
            }
            return route;
        }

        if (route.path === ROUTE_ACCOUNT_DATA.path) {
            route.infoBadgeCount = customerData.content?.documentsRequiredCount ?? 0;
        }

        switch (route.path) {
            case downloadInfoTopics.parent.path:
                return downloadInfoTopics.parent;
            case videoTopicRoutes.parent.path:
                return videoTopicRoutes.parent;
            case successListTopicRoutes.parent.path:
                return successListTopicRoutes.parent;
            default:
                return route;
        }
    });

    const routes = allowedAccountRoutePaths
        ? allRoutes.filter(route => {
              const path = Array.isArray(route) ? route[0].path : route.path;
              return allowedAccountRoutePaths.includes(path);
          })
        : allRoutes;

    return {
        routes,
        downloadInfoTopicRoutes: downloadInfoTopics.routes,
        firstDownloadInfoTopicRoute: downloadInfoTopics.first,
        videoTopicRoutes: videoTopicRoutes.routes,
        firstVideoTopicRoute: videoTopicRoutes.first,
        successListTopicsRoutes: successListTopicRoutes.routes,
        firstSuccessListTopicRoute: successListTopicRoutes.first,
    };
}

export function useAccountActionCount(): number {
    const { routes } = useAccountRoutes();

    return routes
        .map(route => (Array.isArray(route) ? route[0] : route))
        .reduce((sum, route) => sum + (route.infoBadgeCount ?? 0), 0);
}
